import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import './QueriesPage.css';

function QueriesPage() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newQuery, setNewQuery] = useState('');
  const [newQuerySource, setNewQuerySource] = useState('reddit');
  const [queryStats, setQueryStats] = useState({});

  useEffect(() => {
    fetchQueries();
    fetchQueryStats();
  }, []);

  const fetchQueries = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/queries');
      const data = await response.json();
      
      if (data.success) {
        setQueries(data.queries);
      } else {
        setError(data.message || 'Failed to fetch queries');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchQueryStats = async () => {
    try {
      const response = await fetch('/api/opportunities');
      const data = await response.json();
      
      if (data.success) {
        // Calculate stats for each query
        const stats = {};
        
        data.opportunities.forEach(batch => {
          batch.results.forEach(queryResult => {
            const query = queryResult.query;
            const resultCount = queryResult.results.length;
            
            if (!stats[query]) {
              stats[query] = {
                totalUses: 0,
                totalResults: 0,
                lastUsed: null,
                avgResults: 0,
                batches: []
              };
            }
            
            stats[query].totalUses += 1;
            stats[query].totalResults += resultCount;
            stats[query].batches.push({
              date: batch.timestamp,
              results: resultCount
            });
            
            const lastUsed = new Date(batch.timestamp);
            if (!stats[query].lastUsed || lastUsed > new Date(stats[query].lastUsed)) {
              stats[query].lastUsed = batch.timestamp;
            }
          });
        });
        
        // Calculate averages
        Object.keys(stats).forEach(query => {
          stats[query].avgResults = stats[query].totalResults / stats[query].totalUses;
        });
        
        setQueryStats(stats);
      }
    } catch (err) {
      console.error('Error fetching query stats:', err);
    }
  };

  const handleAddQuery = async (e) => {
    e.preventDefault();
    if (!newQuery.trim()) return;

    try {
      const response = await fetch('/api/queries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          query: newQuery.trim(),
          source: newQuerySource 
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setNewQuery('');
        fetchQueries();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    }
  };

  const handleToggleQuery = async (id, active) => {
    try {
      const response = await fetch('/api/queries', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, active })
      });

      const data = await response.json();
      
      if (data.success) {
        fetchQueries();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    }
  };

  const handleDeleteQuery = async (id) => {
    if (!window.confirm('Are you sure you want to delete this query?')) return;

    try {
      const response = await fetch('/api/queries', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id })
      });

      const data = await response.json();
      
      if (data.success) {
        fetchQueries();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    }
  };

  const handleResetQueries = async () => {
    if (!window.confirm('Are you sure you want to reset all queries to the new defaults? This will replace all existing queries.')) return;

    try {
      const response = await fetch('/api/queries', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset: 'all' })
      });

      const data = await response.json();
      
      if (data.success) {
        alert('Queries reset successfully!');
        fetchQueries();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (err) {
      alert('Network error: ' + err.message);
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const getPerformanceRating = (avgResults) => {
    if (avgResults >= 5) return { label: 'Excellent', class: 'excellent' };
    if (avgResults >= 3) return { label: 'Good', class: 'good' };
    if (avgResults >= 1) return { label: 'Fair', class: 'fair' };
    return { label: 'Poor', class: 'poor' };
  };

  if (loading) {
    return (
      <div className="queries-page">
        <Navigation />
        <div className="container">
          <div className="loading-state">
            <h1>Loading queries...</h1>
            <p>Please wait while we fetch your search queries</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="queries-page">
        <Navigation />
        <div className="container">
          <div className="error-state">
            <h1>Error</h1>
            <p>{error}</p>
            <button onClick={fetchQueries} className="retry-btn">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activeQueries = queries.filter(q => q.active);
  const inactiveQueries = queries.filter(q => !q.active);

  return (
    <div className="queries-page">
      <Navigation />
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <h1>Search Queries</h1>
            <p>Manage your search queries and view their performance metrics</p>
          </div>
          <div className="header-actions">
            <button onClick={handleResetQueries} className="reset-queries-btn">
              🔄 Reset to New Defaults
            </button>
            <a href="/opportunities" className="opportunities-link">
              🎯 View Opportunities
            </a>
          </div>
        </div>

        {/* Add New Query */}
        <div className="add-query-section">
          <h2>Add New Query</h2>
          <form onSubmit={handleAddQuery} className="add-query-form">
            <input
              type="text"
              value={newQuery}
              onChange={(e) => setNewQuery(e.target.value)}
              placeholder="Enter a new search query (e.g., 'rolex accuracy issues')"
              className="query-input"
            />
            <select
              value={newQuerySource}
              onChange={(e) => setNewQuerySource(e.target.value)}
              className="source-select"
            >
              <option value="reddit">Reddit</option>
              <option value="google">Google</option>
            </select>
            <button type="submit" className="add-query-btn">
              Add Query
            </button>
          </form>
        </div>

        {/* Query Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-number">{activeQueries.length}</div>
            <div className="stat-label">Active Queries</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{inactiveQueries.length}</div>
            <div className="stat-label">Inactive Queries</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {Object.values(queryStats).reduce((sum, stat) => sum + stat.totalResults, 0)}
            </div>
            <div className="stat-label">Total Results</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">
              {Object.values(queryStats).reduce((sum, stat) => sum + stat.totalUses, 0)}
            </div>
            <div className="stat-label">Total Uses</div>
          </div>
        </div>

        {/* Active Queries */}
        <div className="queries-section">
          <h2>Active Queries ({activeQueries.length})</h2>
          <div className="queries-list">
            {activeQueries.map((query) => {
              const stats = queryStats[query.query] || { totalUses: 0, totalResults: 0, avgResults: 0 };
              const performance = getPerformanceRating(stats.avgResults);
              
              return (
                <div key={query.id} className="query-item active">
                  <div className="query-header">
                    <div className="query-info">
                      <h3>{query.query}</h3>
                      <div className="query-meta">
                        <span className={`source-badge ${query.source || 'reddit'}`}>
                          {query.source || 'Reddit'}
                        </span>
                        <span className="query-date">
                          Added: {formatDate(query.createdAt)}
                        </span>
                        {stats.lastUsed && (
                          <span className="query-last-used">
                            Last used: {formatDate(stats.lastUsed)}
                          </span>
                        )}
                        {query.isDefault && (
                          <span className="default-badge">Default</span>
                        )}
                      </div>
                    </div>
                    <div className="query-actions">
                      <button 
                        onClick={() => handleToggleQuery(query.id, false)}
                        className="toggle-btn active"
                        title="Deactivate query"
                      >
                        ✓ Active
                      </button>
                      {!query.isDefault && (
                        <button 
                          onClick={() => handleDeleteQuery(query.id)}
                          className="delete-btn"
                          title="Delete query"
                        >
                          🗑️ Delete
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="query-stats">
                    <div className="stat-item">
                      <div className="stat-value">{stats.totalUses}</div>
                      <div className="stat-label">Times Used</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{stats.totalResults}</div>
                      <div className="stat-label">Total Results</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{stats.avgResults.toFixed(1)}</div>
                      <div className="stat-label">Avg Results</div>
                    </div>
                    <div className="stat-item">
                      <div className={`performance-badge ${performance.class}`}>
                        {performance.label}
                      </div>
                      <div className="stat-label">Performance</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Inactive Queries */}
        {inactiveQueries.length > 0 && (
          <div className="queries-section">
            <h2>Inactive Queries ({inactiveQueries.length})</h2>
            <div className="queries-list">
              {inactiveQueries.map((query) => {
                const stats = queryStats[query.query] || { totalUses: 0, totalResults: 0, avgResults: 0 };
                const performance = getPerformanceRating(stats.avgResults);
                
                return (
                  <div key={query.id} className="query-item inactive">
                    <div className="query-header">
                      <div className="query-info">
                        <h3>{query.query}</h3>
                        <div className="query-meta">
                          <span className={`source-badge ${query.source || 'reddit'}`}>
                            {query.source || 'Reddit'}
                          </span>
                          <span className="query-date">
                            Added: {formatDate(query.createdAt)}
                          </span>
                          {stats.lastUsed && (
                            <span className="query-last-used">
                              Last used: {formatDate(stats.lastUsed)}
                            </span>
                          )}
                          {query.isDefault && (
                            <span className="default-badge">Default</span>
                          )}
                        </div>
                      </div>
                      <div className="query-actions">
                        <button 
                          onClick={() => handleToggleQuery(query.id, true)}
                          className="toggle-btn inactive"
                          title="Activate query"
                        >
                          ○ Inactive
                        </button>
                        {!query.isDefault && (
                          <button 
                            onClick={() => handleDeleteQuery(query.id)}
                            className="delete-btn"
                            title="Delete query"
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="query-stats">
                      <div className="stat-item">
                        <div className="stat-value">{stats.totalUses}</div>
                        <div className="stat-label">Times Used</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">{stats.totalResults}</div>
                        <div className="stat-label">Total Results</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">{stats.avgResults.toFixed(1)}</div>
                        <div className="stat-label">Avg Results</div>
                      </div>
                      <div className="stat-item">
                        <div className={`performance-badge ${performance.class}`}>
                          {performance.label}
                        </div>
                        <div className="stat-label">Performance</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {queries.length === 0 && (
          <div className="empty-state">
            <h2>No Queries Yet</h2>
            <p>Add your first search query to get started!</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default QueriesPage; 